import {useMutation} from '@tanstack/react-query'
import {externalApiURL} from '@yandex-delivery/client-core/api/corp'
import {useCallback, useId, useMemo, useState} from 'react'
import {useFormContext} from 'react-hook-form'

import {useConfigContext} from '../../../../contexts/ConfigContext'
import type {Contact} from '../../../../types'
import type {
  CallbackHandlers,
  OptimizeRouteResultResponse,
  OptimizeRouteStartRequest,
  Point,
  RouteOptimizationState,
} from './types'
import {fetchOptimizedRoute, toDestinationRouteChangePoint, toSourceRouteChangePoint} from './utils'

export function useOptimizeRouteConfirmModal(setActive?: (isActive: boolean) => void): {
  optimizationState: RouteOptimizationState
  onDecline: VoidFunction
  onAccept: VoidFunction
} {
  const [optimizationState, setOptimizationState] = useState<RouteOptimizationState>('idle')

  const {watch} = useFormContext()
  const routePoints = watch('route')
  const destinationPoints: Point[] = Object.keys(routePoints).flatMap(key =>
    key === 'source' || key === 'return' ? [] : {...routePoints[key], _id: key},
  )
  const {fetchOptimisedRouteHandler} = useFetchOptimizedRoute()
  const {destinationPointsUpdateHandler} = useUpdateDestinationPoints()

  const successFetchOptimizationRouteHandler = useCallback(
    (route?: OptimizeRouteResultResponse) => {
      destinationPointsUpdateHandler(route?.result?.route)
      setOptimizationState('completed')
      setActive?.(false)
    },
    [destinationPointsUpdateHandler, setActive],
  )

  const route: Point[] = useMemo(() => {
    const sourcePoint: Point = {...routePoints.source, _id: 'source'}
    return [toSourceRouteChangePoint(sourcePoint), ...destinationPoints.map(toDestinationRouteChangePoint)]
  }, [destinationPoints, routePoints])

  const onAccept = useCallback(() => {
    fetchOptimisedRouteHandler(
      {
        route,
      },
      {
        onPending: () => {
          setOptimizationState('pending')
        },
        onError: () => {
          setOptimizationState('error')
        },
        onSuccess: successFetchOptimizationRouteHandler,
      },
    )
  }, [fetchOptimisedRouteHandler, route, successFetchOptimizationRouteHandler])

  const onDecline = useCallback(() => {
    if (setActive) {
      // костыли из-за протекания событий в GWC
      setTimeout(() => {
        setActive(false)
      }, 1000 / 60)
    }
  }, [setActive])

  return {
    optimizationState,
    onDecline,
    onAccept,
  }
}

export function useFetchOptimizedRoute(): {
  optimisedRoute: OptimizeRouteResultResponse | undefined
  isOptimisedRoutePending: boolean
  fetchOptimisedRouteHandler: (body: OptimizeRouteStartRequest, handlers: CallbackHandlers) => void
} {
  const {clientId = '', countryCode} = useConfigContext()
  const key = useId()

  const mutation = useMutation({
    mutationKey: [`optimised-route-${key}`],
    mutationFn: (body: OptimizeRouteStartRequest) => fetchOptimizedRoute(clientId, body, countryCode, externalApiURL),
  })

  const {data: optimisedRoute, isPending: isOptimisedRoutePending} = mutation

  const fetchOptimisedRouteHandler = useCallback(
    (body: OptimizeRouteStartRequest, handlers: CallbackHandlers) => {
      handlers.onPending()

      mutation.mutate(body, {
        onSuccess: response => {
          handlers.onSuccess(response)
        },
        onError: () => {
          handlers.onError()
        },
      })
    },
    [mutation],
  )

  return {
    optimisedRoute,
    isOptimisedRoutePending,
    fetchOptimisedRouteHandler,
  }
}

function useUpdateDestinationPoints(): {destinationPointsUpdateHandler: (route?: string[]) => void} {
  const {watch, setValue} = useFormContext()
  const name = 'route'

  function destinationPointsUpdateHandler(route?: string[]): void {
    const routePoints = watch(name)
    const contacts = watch('contacts')
    const sourceContact = contacts.find((contact: Contact) => contact.pointId === 'source')

    const newRoute = {
      source: routePoints.source,
      return: routePoints.return,
    }

    const newContacts = [sourceContact]

    route?.forEach((pointId: string, index: number) => {
      if (pointId === 'source') return

      /** переименовываем точки и связанные с ними контакты */
      const newName = `destination_${index}`
      newRoute[newName] = routePoints[pointId]

      /** копируем контакт, потому что дальше будем его мутировать */
      const newContact = {...contacts.find((contact: Contact) => contact.pointId === pointId)}
      newContact.pointId = newName
      newContacts.push(newContact)
    })

    setValue(name, newRoute)
    setValue('contacts', newContacts)

    /** индикатор для конфига кнопки оптимизации маршрута, чтобы рисовать там галочку или стрелочку */
    setValue('is_route_optimized', true)
  }

  return {
    destinationPointsUpdateHandler,
  }
}
